import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface ProgramEnrollment {
  id: string;
  sessions_remaining: number;
  mentor_programs: {
    title: string;
    session_count: number;
  };
}

interface ProgramSessionSelectorProps {
  mentorId: string;
  onProgramSelect: (enrollmentId: string | null) => void;
  selectedProgramId: string | null;
}

export const ProgramSessionSelector = ({
  mentorId,
  onProgramSelect,
  selectedProgramId,
}: ProgramSessionSelectorProps) => {
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, [mentorId]);

  const fetchEnrollments = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('program_enrollments')
        .select(`
          id,
          sessions_remaining,
          mentor_programs (
            title,
            session_count
          )
        `)
        .eq('student_id', session.user.id)
        .eq('mentor_id', mentorId)
        .eq('is_active', true)
        .gt('sessions_remaining', 0);

      if (error) throw error;
      setEnrollments(data || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || enrollments.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="w-4 h-4" />
          Use Program Session?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <RadioGroup
          value={selectedProgramId || 'regular'}
          onValueChange={(value) => onProgramSelect(value === 'regular' ? null : value)}
        >
          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="regular" id="regular" />
            <Label htmlFor="regular" className="flex-1 cursor-pointer">
              <div className="font-medium">Regular Session</div>
              <div className="text-xs text-muted-foreground">Pay with tokens</div>
            </Label>
          </div>

          {enrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <RadioGroupItem value={enrollment.id} id={enrollment.id} />
              <Label htmlFor={enrollment.id} className="flex-1 cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{enrollment.mentor_programs.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {enrollment.sessions_remaining} of {enrollment.mentor_programs.session_count} sessions left
                    </div>
                  </div>
                  {selectedProgramId === enrollment.id && (
                    <Badge variant="default" className="ml-2">
                      <Check className="w-3 h-3 mr-1" />
                      Selected
                    </Badge>
                  )}
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>

        {selectedProgramId && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-green-700 dark:text-green-300">
                No tokens will be charged for this booking. One session from your program will be used.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
